/**
 * Voice-conversation recording/playback: native (`cordova-plugin-media` +
 * `cordova-plugin-file`) on a real device, with a browser fallback
 * (`MediaRecorder` + `<audio>`) so the same feature works in the browser/
 * PWA build too — same "native, with a real browser fallback behind one
 * call site" pattern already used by `httpClient.ts` (`httpUploadBytes`/
 * `fetchUploadBytes`) and `filePicker.ts`.
 *
 * The native path is structurally reviewed only (neither plugin exists
 * outside a real device/WebView, so it can't be exercised here); the
 * browser path uses only standard, verifiable Web APIs.
 */

// cordova-plugin-media's global constructor; no first-party TS types in
// this project, declared narrowly here rather than as an ambient global.
declare const Media: new (
  src: string,
  mediaSuccess: () => void,
  mediaError?: (err: unknown) => void,
) => {
  startRecord(): void;
  stopRecord(): void;
  play(): void;
  stop(): void;
  release(): void;
};

// Real bug (2026-09-2x): a PWA/browser voice request failed with
// "The operation is not supported" -- the exact WebKit DOMException
// message for a NotSupportedError, and `new MediaRecorder(stream)` with
// no explicit `mimeType` is a well-documented case that throws it on
// Safari, which (unlike Chromium/Firefox, where a bare `webm`/`opus`
// default just works) doesn't reliably support the same implicit
// default. Explicitly probing a list of real-world-supported candidates
// and passing the first supported one is the standard cross-browser
// fix. `audio/mp4` first since that's Safari's actual native recording
// container; the rest cover Chromium/Firefox.
const AUDIO_MIME_TYPE_CANDIDATES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  return AUDIO_MIME_TYPE_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

function isNativeMediaAvailable(): boolean {
  const w = window as any;
  if (w.cordova?.platformId === 'browser') {
    // Same class of bug already confirmed for cordova-plugin-advanced-http
    // (httpClient.ts) and cordova-plugin-chooser (filePicker.ts): some
    // Cordova plugins' JS wrapper is bundled into `cordova build browser`
    // output even though the plugin itself has no real implementation
    // there. Not directly confirmed for cordova-plugin-media specifically,
    // but treated the same way defensively, consistent with those two
    // confirmed cases rather than assuming this one plugin is different.
    return false;
  }
  return typeof Media !== 'undefined';
}

function cordovaFile(): { cacheDirectory: string } {
  const file = (window as any).cordova?.file;
  if (!file) {
    throw new Error(
      'cordova-plugin-file is required for voice recording/playback — not available in the browser dev build.',
    );
  }
  return file;
}

// cordova-plugin-media requires a specific extension per platform:
// Android records AAC ADTS (.aac); iOS records .wav or .m4a. Getting
// this wrong doesn't fail loudly — the plugin's own docs note this is a
// hard platform requirement, not a suggestion.
function recordingExtension(): string {
  const platformId = (window as any).cordova?.platformId;
  return platformId === 'ios' ? 'wav' : 'aac';
}

/** What one completed recording produced — a discriminated union rather
 * than always a file path, since a browser recording exists only as
 * in-memory bytes (no native filesystem to hand back a path into).
 * `DomiHealthApi.transcribeAudio` (native path) vs `transcribeAudioBytes`
 * (browser path) is the corresponding split on the upload side. */
export type RecordedAudio = { fileUri: string } | { data: Uint8Array; mediaType: string };

/** One recording session: construct, `start()`, then `stop()` to get
 * back the recorded audio (a native file path, or in-memory bytes in a
 * browser). Throws synchronously from the constructor if neither the
 * native plugin nor the browser's own recording APIs are available at
 * all (e.g. a very old browser, or no microphone permission API). */
export class VoiceRecording {
  private nativeMedia?: InstanceType<typeof Media>;
  private nativeFilePath?: string;
  private mediaRecorder?: MediaRecorder;
  private mediaStream?: MediaStream;
  private chunks: Blob[] = [];

  constructor() {
    if (isNativeMediaAvailable()) {
      this.nativeFilePath = `${cordovaFile().cacheDirectory}domihealth-recording-${Date.now()}.${recordingExtension()}`;
      this.nativeMedia = new Media(this.nativeFilePath, () => {}, (err: unknown) => {
        // Media's error callback is asynchronous and has no clean way to
        // reject an already-returned promise from start()/stop() — logged
        // rather than silently swallowed, same honesty-over-completeness
        // trade-off as elsewhere in this native-only, unverifiable layer.
        // eslint-disable-next-line no-console
        console.error('VoiceRecording error:', err);
      });
      return;
    }
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw new Error('Voice recording is not supported in this browser.');
    }
    // The actual microphone permission prompt + stream/recorder setup is
    // async (getUserMedia), so it happens in start(), not here.
  }

  async start(): Promise<void> {
    if (this.nativeMedia) {
      this.nativeMedia.startRecord();
      return;
    }
    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = pickSupportedMimeType();
    this.mediaRecorder = mimeType ? new MediaRecorder(this.mediaStream, { mimeType }) : new MediaRecorder(this.mediaStream);
    this.chunks = [];
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };
    this.mediaRecorder.start();
  }

  /** Stops recording and releases the native/browser resource, resolving
   * with the recorded audio. */
  stop(): Promise<RecordedAudio> {
    if (this.nativeMedia && this.nativeFilePath) {
      this.nativeMedia.stopRecord();
      this.nativeMedia.release();
      return Promise.resolve({ fileUri: this.nativeFilePath });
    }
    const recorder = this.mediaRecorder;
    const stream = this.mediaStream;
    if (!recorder) {
      return Promise.reject(new Error('Recording was never started.'));
    }
    return new Promise((resolve) => {
      recorder.onstop = async () => {
        const mediaType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(this.chunks, { type: mediaType });
        const data = new Uint8Array(await blob.arrayBuffer());
        stream?.getTracks().forEach((track) => track.stop());
        resolve({ data, mediaType });
      };
      recorder.stop();
    });
  }
}

/** Plays a local audio file (e.g. one just written by
 * `saveAudioToFile`) and resolves once playback finishes. Native only —
 * `playSynthesizedAudio` below is the cross-platform entry point every
 * caller should actually use. */
export function playAudioFile(filePath: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const media = new Media(
      filePath,
      () => {
        media.release();
        resolve();
      },
      (err: unknown) => {
        media.release();
        reject(new Error(`Playback failed: ${JSON.stringify(err)}`));
      },
    );
    media.play();
  });
}

/** Writes the edge node's synthesized audio (from
 * `DomiHealthApi.synthesizeSpeech`) to a local file so `playAudioFile`
 * can play it — standard cordova-plugin-file resolveLocalFileSystemURL
 * -> getFile -> createWriter chain. Native only.
 *
 * Resolves with a path built the same way `VoiceRecording`'s native
 * path is (`cacheDirectory` + filename), NOT `fileEntry.toURL()` — on
 * Android, `toURL()` can return an abstract `cdvfile://localhost/cache/...`
 * URL rather than a real `file://` path, which `cordova-plugin-media`'s
 * Android player cannot open (surfaces as a generic
 * `AudioPlayer.onError (1, -2147483648)` "system" error, since the
 * literal string isn't a filesystem path it can read). The manually
 * built path is already proven to work with `Media()` for recording. */
export function saveAudioToFile(data: ArrayBuffer, contentType: string): Promise<string> {
  const extension = contentType.includes('wav') ? 'wav' : 'aiff';
  const fileName = `domihealth-response-${Date.now()}.${extension}`;
  const filePath = `${cordovaFile().cacheDirectory}${fileName}`;

  return new Promise((resolve, reject) => {
    const win = window as any;
    win.resolveLocalFileSystemURL(
      cordovaFile().cacheDirectory,
      (dirEntry: any) => {
        dirEntry.getFile(
          fileName,
          { create: true, exclusive: false },
          (fileEntry: any) => {
            fileEntry.createWriter(
              (fileWriter: any) => {
                fileWriter.onwriteend = () => resolve(filePath);
                fileWriter.onerror = (err: unknown) =>
                  reject(new Error(`Failed to write audio file: ${JSON.stringify(err)}`));
                fileWriter.write(new Blob([data], { type: contentType }));
              },
              (err: unknown) => reject(new Error(`Failed to create file writer: ${JSON.stringify(err)}`)),
            );
          },
          (err: unknown) => reject(new Error(`Failed to create audio file: ${JSON.stringify(err)}`)),
        );
      },
      (err: unknown) => reject(new Error(`Failed to resolve cache directory: ${JSON.stringify(err)}`)),
    );
  });
}

// Real bug (2026-09-2x): a PWA voice request transcribed and got a real
// chat reply correctly, but response playback failed with "The
// operation is not supported" -- the exact WebKit DOMException a
// blob: URL <audio> source rejects `.play()` with. This is a real,
// well-documented WebKit bug: Safari plays a blob: URL audio source
// unreliably specifically in standalone "Add to Home Screen" PWA mode
// (a more restrictive resource-loading context than a regular Safari
// tab), even though the same audio plays fine from a normal URL. A
// base64 data: URI sidesteps that broken code path entirely -- the
// standard, widely-documented workaround for this exact class of bug.
function bytesToDataUri(data: ArrayBuffer, contentType: string): string {
  const bytes = new Uint8Array(data);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `data:${contentType};base64,${btoa(binary)}`;
}

/** Browser fallback for playing synthesized speech: a real browser can
 * play audio directly via a plain `<audio>` element, with no
 * virtual-filesystem write step needed at all (unlike native, which has
 * no way to hand `cordova-plugin-media` an in-memory buffer directly —
 * it only ever plays from a path). Uses a data: URI, not a blob: URL —
 * see the comment above `bytesToDataUri`. */
function playAudioBytesInBrowser(data: ArrayBuffer, contentType: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio = new Audio(bytesToDataUri(data, contentType));
    audio.onended = () => resolve();
    audio.onerror = () => reject(new Error('Browser audio playback failed.'));
    audio.play().catch(reject);
  });
}

/** The cross-platform entry point every caller should use to play a
 * `DomiHealthApi.synthesizeSpeech` result — hides the native
 * (save-to-file, then play) vs. browser (play a Blob URL directly)
 * split behind one call, same pattern as `httpClient.ts`'s
 * `httpUploadBytes`. */
export async function playSynthesizedAudio(data: ArrayBuffer, contentType: string): Promise<void> {
  if (!isNativeMediaAvailable()) {
    return playAudioBytesInBrowser(data, contentType);
  }
  const filePath = await saveAudioToFile(data, contentType);
  await playAudioFile(filePath);
}
